<template>
    <div class="page">
        <NuxtErrorBoundary @error="showError">
            <template v-if="!isEmbedded">
                <main class="page-container">
                    <NuxtLoadingIndicator
                        :throttle="200"
                        :duration="2000"
                        :color="colors.primary"
                    />
                    <DSNotifications />
                    <LayoutMainHeader :wrapper-classes="wrapperClasses" :is-large-desktop="isLargeDesktop">
                        <template #left>
                            <nuxt-link class="logo" :to="$paths.getPath('landingPage')">
                                <object v-bind="logo" />
                            </nuxt-link>
                        </template>
                        <template #center>
                            <LayoutMainNavigationDesktop
                                class="main-nav"
                                :links="navigationOne"
                            />
                        </template>
                        <template v-if="$bp.bps.isDesktop" #right>
                            <LayoutSearch />
                            <LayoutQuickLinks :links="layoutData.navigation" />
                            <LayoutUserMenu :is-visible="userMenu" :customer-type="customerType" />
                        </template>
                        <template
                            v-if="navigationOne?.find(item => item.url.includes(routeArray.slice(0, 2).join('/')))?.children"
                            #bottom
                        >
                            <LayoutMainNavigationDesktop
                                v-if="routeArray.length > 1"
                                class="main-nav"
                                :links="navigationOne?.find(item => item.url.includes(routeArray.slice(0, 2).join('/')))?.children.map(markSelected)"
                            />
                        </template>
                    </LayoutMainHeader>
                    <DSSidebar class="lenis-ignore">
                        <template #sidebar-right>
                            <LayoutMainNavigationMobile :links="navigationTree" :user-menu="userMenu" />
                        </template>
                    </DSSidebar>
                    <NuxtPage
                        class="page-content"
                        :transition="{
                            onBeforeEnter,
                        }"
                    />
                    <Teleport v-if="modal.name === 'emergencyModal'" :to="modal.modalTarget">
                        <div class="ds-modal-content">
                            <div class="head">
                                <div class="head-content">{{ layoutData.emergencyModal.title }}</div>
                                <button class="close-btn" @click="modal.unsetNameAndClose">
                                    <DSIcon
                                        code="close"
                                        size="xs"
                                    />
                                </button>
                            </div>
                            <div class="body">
                                <DSCFText :content="layoutData.emergencyModal.text" class="ds-container text-center" />
                            </div>
                            <div class="foot">
                                <div class="text-center">
                                    <button
                                        class="ds-btn ds-btn--primary"
                                        @click="modal.unsetNameAndClose"
                                    >
                                        {{ $t('emergencyModal.close') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Teleport>
                    <DSModal />
                    <LayoutQuickNavigation />
                </main>
                <LayoutFooter
                    :icons="layoutData?.icons"
                    :items="layoutData?.footerLinks"
                    :download="layoutData?.downloadLinks"
                    :some="layoutData?.someLinks"
                    :extras="layoutData?.extraLinks"
                />
            </template>
            <template v-else>
                <NuxtPage
                    :transition="{
                        onBeforeEnter,
                    }"
                />
            </template>
        </NuxtErrorBoundary>
    </div>
</template>
<script setup>
import { useLayoutStore } from '~dsc/stores/cms/layout';
import { usePageStore } from '~dsc/stores/cms/page';
import { useCartStore } from '~dsc/stores/cms/cart';
import {
    ref,
    computed,
    onMounted,
    provide,
    watch,
    useNuxtApp,
    useI18n,
    useRoute,
    useAnalytics,
    useLayoutLoad,
    showError,
} from '#imports';
import { storeToRefs } from 'pinia';
import * as websiteTokens from '~dsc/assets/scss/themes/st1-website/_export.module.scss';
import 'lenis/dist/lenis.css';
import { useModalStore } from '~dsc/stores/modal';
import Lenis from 'lenis';
import gtmPerLocale from '@@/configuration/gtm.json';

const { finalizePendingLocaleChange } = useI18n();
const { layoutData, navigationTree } = storeToRefs(useLayoutStore());
const { $paths, $bp, $i18n } = useNuxtApp();
const { pageData } = storeToRefs(usePageStore());
const { initGtm } = useAnalytics();
const route = useRoute();
const modal = useModalStore();

if (import.meta.client) {
    try {
        await useLayoutLoad();
    } catch (error) {
        console.error('Layout load failed:', error);
    }
}

initGtm({ id: gtmPerLocale[$i18n.locale.value] });

const colors = {
    'primary': websiteTokens.default['color-primary-500'],
};

const scrollY = ref(0);
const scrollLimit = ref(150);

const isEmbedded = computed(() => Number(route.query?.embed) === 1);
const firstBlockFullScreen = computed(() => pageData.value?.[0]?.display?.type === 'fullscreen');
const userMenu = computed(() => {
    const [ , section ] = route.path.split('/');

    if ($i18n.locale.value === 'sv-SE' && section !== 'foretag') return false;
    if ($i18n.locale.value === 'no-NO' && section !== 'bedrift') return false;
    if ($i18n.locale.value === 'en-EU') return false;

    return true;
});
const layout = ref({
    theme: firstBlockFullScreen.value === true ? 'clear' : 'white',
    sidebars: {
        left: false,
        right: false,
    },
    button: {
        ctaIcon: 'arrow-right',
    },
});

const routeArray = computed(() => {
    const routeArray = route.path.split('/');

    return routeArray.filter(value => value !== '' && value !== 'preview');
});

const navigationOne = computed(() => {
    if (!navigationTree.value.map(item => item.url).includes(routeArray.value[0])) {
        return navigationTree.value?.[0]?.children.map(markSelected) ?? [];
    }

    return navigationTree.value.find(item => item.url === routeArray.value[0])?.children.map(markSelected) ?? [];
});
const customerType = computed(() => $paths.getPath('b2b') === `/${routeArray.value[0]}` ? 'B2B' : 'B2C');

const onBeforeEnter = async () => {
    await finalizePendingLocaleChange();
    usePageStore().present();
    layout.value.theme = firstBlockFullScreen.value === true ? 'clear' : 'white';
};

onMounted(async () => {
    if (layoutData?.value?.emergencyModal) modal.setNameAndOpen('emergencyModal');
    window.addEventListener('scroll', handleScroll);
    await useCartStore().loadFromLocal();
    const ignoredScrollContainers = [ 'station-locator', 'lenis-ignore', 'ds-select' ];
    new Lenis({
        autoRaf: true,
        prevent: node => ignoredScrollContainers.some(selector => node.classList.contains(selector)),
    });
});

const markSelected = item => {
    const urlArray = item.url.split('/');
    const currentLevel = routeArray.value.slice(0, urlArray.length);

    return {
        ...item,
        active: urlArray.join('/') === currentLevel.join('/'),
    };
};

function handleScroll() {
    scrollY.value = window.scrollY;
}

const logo = computed(() => {
    if (!layoutData.logo) return { data: '/images/logo.svg', alt: 'St1' };
    const { 0: logo } = layoutData.logo;

    return logo;
});

const isLargeDesktop = computed(() => $bp.viewportWidth.value >= 1440);

const isScrolledPastStickyTreshold = computed(() => scrollY.value > scrollLimit.value);

watch(() => scrollY.value, () => {
    if (isScrolledPastStickyTreshold.value && layout.value.theme === 'clear' || firstBlockFullScreen.value === false) {
        layout.value.theme = 'white';
    } else if (!isScrolledPastStickyTreshold.value && layout.value.theme === 'white') {
        layout.value.theme = 'clear';
    }
});

watch(() => pageData.value?.length, () => {
    layout.value.theme = firstBlockFullScreen.value === true ? 'clear' : 'white';
});

const wrapperClasses = computed(() => ({
    'is-sticky': isScrolledPastStickyTreshold.value,
}));

useI18n({
    useScope: 'global',
    messages: {
        'fi-FI': {
            login: {
                link: 'Kirjaudu',
            },
            additionalLinks: {
                carwash: 'Autopesulat',
            },
            navigation: {
                search: 'Search',
            },
            emergencyModal: {
                close: 'Sulje',
            },
        },
        'en-EU': {
            login: {
                link: 'Login',
            },
            additionalLinks: {
                carwash: 'Carwashes',
            },
            navigation: {
                search: 'Search',
            },
            emergencyModal: {
                close: 'Close',
            },
        },
    },
});

provide('layout', layout);
</script>
<style lang="scss">
header {
    z-index: 5;
}

.header .bottom {
    transition: margin .275s;
}

.header.is-sticky {
    .content {
        padding-top: .625rem;
        padding-bottom: .75rem;
    }

    .bottom:not(:empty) {
        margin-top: 1.375rem;
    }
}

.page-content {
    position: relative;
    display: flex;
    flex-flow: row wrap;
    overflow: hidden;
    padding-top: 13.5rem;

    .hero-banner.fullscreen:first-child {
        margin-top: -13.5rem;
    }

    @include respond-to('at least desktop') {
        .station-locator:first-child {
            margin-top: -8rem;
        }
    }
}

.page {
    display: grid;
    min-height: 100vh;
    max-width: 100%;
    grid-template-columns: 1fr;
    grid-template-rows: minmax(calc(100vh - 10.75rem), 1fr);
}

.page-container {
    width: 100%;
    overflow-x: hidden;
    min-height: calc(100vh - 4rem);
    position: relative;
    z-index: 1;

    > .notifications {
        position: fixed;
        height: calc(100vh - 20rem);
        width: 90vw;
        padding-block: 1rem;
        max-width: var(--page-max-width);
        transform: translate(-50%, -50%);
        left: 50%;
        top: 50%;
        z-index: 6;
        pointer-events: none;

        .ds-notification {
            pointer-events: auto;
        }
    }
}

.logo {
    display: block;

    object {
        pointer-events: none;
        height: 42px;
        width: auto;
        image-rendering: optimizeQuality;
    }
}

@include respond-to('at most small desktop') {
    .page {
        grid-template-rows: minmax(calc(100vh - 4rem), 1fr);
    }

    .page-content {
        min-height: calc(100vh - 4rem);
        padding-top: 4rem;

        .hero-banner.fullscreen:first-child {
            margin-top: -4rem;
        }
    }
}
</style>
